<template>
  <div class="__BillingReport_Body">
    <CLoading :loading="loading" />

    <template v-if="!loading">
      <div v-if="data.length === 0">
        {{ $t('No data to display.') }}
      </div>

      <template v-else>
        <div class="__BillingReport_Body_Head">
          <!--Month selector-->
          <CSelector
            class="__BillingReport_Body_Head_Selector"
            v-model="currentService"
            :options="serviceOptions"
            emit-value
            map-options
          >
          </CSelector>

          <!--Amount-->
          <div class="__BillingReport_Body_Head_TotalAmount">
            <CLabelValue :label="$t('Service total amount')" align="right" small-label>
              <div class="text-lg">
                <CCurrency
                  :value="reportData.total"
                  :type="currencyType()"
                  :tooltip="`${currencySymbol(currencyType())}${reportData.total}`"
                />
              </div>
            </CLabelValue>
          </div>
        </div>

        <!--Body content-->
        <div class="__BillingReport_Body_Content">
          <div v-if="reportDetails.length === 0">
            {{ $t('No data to display.') }}
          </div>

          <ReportTable :data="reportDetails" v-else />
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapState } from 'vuex';
import { sortObject } from '@/lib/sorter';
import services from '@/services';
import CTitle from '@/components/uikit/CTitle';
import CLabelValue from '@/components/uikit/CLabelValue';
import CSelector from '@/components/uikit/CSelector';
import CCurrency from '@/components/uikit/CCurrency';
import CLoading from '@/components/uikit/CLoading';
import Product from '@/components/Product';
import ReportTable from '@/components/BillingReport/ReportTable';

const messages = {
  messages: {
    en: {
      'Service total amount': 'Service Cost',
      'No data to display.': 'No data to display.',
    },
    ja: {
      'Service total amount': 'サービスの合計金額',
      'No data to display.': '表示可能なデータがありません。',
    },
  },
};

export default Vue.extend({
  name: 'BillingReportBody',
  i18n: messages,
  components: {
    CTitle,
    CLabelValue,
    CSelector,
    CCurrency,
    CLoading,
    Product,
    ReportTable,
  },
  props: {
    data: {
      type: Array,
      default: () => {
        return {};
      },
    },
    loading: {
      type: Boolean,
    },
  },
  data() {
    return {
      currentService: '',
    };
  },
  watch: {
    data(data) {
      if (data.length > 0) {
        this.currentService = data.reduce((max, service) =>
          max.total > service.total ? max : service
        )['service'];
      }
    },
  },
  computed: {
    ...mapState({
      vendor: state => state.vendor,
    }),

    reportData() {
      return this.data.find(v => v.service === this.currentService);
    },

    serviceOptions() {
      const options = this.data.map(item => {
        return {
          label: services(item.service),
          value: item.service,
        };
      });

      return sortObject(options, 'label');
    },

    reportDetails() {
      if (!this.reportData) return [];
      if (!this.reportData['report']) return [];

      const data = this.reportData['report'];
      if (!data) {
        return [];
      }

      let regions = [];
      data.forEach(value => {
        regions.push(value.region);
      });
      // Regionsの重複を削除
      regions = Array.from(new Set(regions));

      // Region毎にデータを整理
      let result = [];
      regions.forEach(region => {
        const x = [];
        data.forEach(value => {
          if (value.region === region) {
            x.push(value);
          }
        });
        result.push({
          region: region,
          data: x,
        });
      });

      return result;
    },
  },
  methods: {
    currencyType() {
      return this.vendor === 'aws' || this.vendor === 'azure' ? 'usd' : 'jpy';
    },

    currencySymbol(currency) {
      switch (currency) {
        case 'usd':
          return '$';
        case 'jpy':
          return '¥';
        default:
          return '';
      }
    },
  },
});
</script>

<style lang="scss" scoped>
.__BillingReport_Body {
  position: relative;
  min-height: 160px;

  &_Head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: $space-md;

    &_Selector {
      flex-basis: 250px;
    }

    &_TotalAmount {
      flex-basis: auto;
      text-align: right;
    }
  }
}
</style>
